import * as React from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import GroupForm from 'modules/dashboard/components/GroupForm'
import groups from 'modules/dashboard/store/collections/communities/Groups'

interface Props {
  onClose: () => void
  groupId: number
}

function EditGroup({ onClose, groupId }: Props) {
  const group = groups.find({
    id: groupId,
  })

  const hasGroup = !!group

  const form = React.useRef(
    new Form(
      {
        title: group?.attributes.title,
        description: group?.attributes.description,
      },
      {
        title: 'string',
        description: 'string',
      }
    )
  ).current

  const { loading } = useAsyncData(async () => {
    if (hasGroup) return

    await groups.fetch({
      data: {
        id: groupId,
      },
      remove: false,
    })

    const fetchedGroup = groups.find({
      id: groupId,
    })

    if (!fetchedGroup) return

    form.setValues({
      title: fetchedGroup.attributes.title,
      description: fetchedGroup.attributes.description,
    })
  }, [groupId])

  const submit = async (closePortal: ClosePortalFn) => {
    if (!group) return

    const { success } = await asyncAction(() => group.save(form.data()))

    if (success) closePortal()
  }

  const showLoader = loading && !hasGroup

  if (showLoader) return <Loading />

  if (!group) return null

  const disabled = !form.get('title').value || !form.get('description').value

  return (
    <Modal
      description={i18n.t('dashboard.groups.edit.description')}
      illustration="people-add"
      onClose={onClose}
      onSubmit={submit}
      title={i18n.t('dashboard.groups.edit.title')}
      footer={
        <Button disabled={disabled} submit>
          {i18n.t('dashboard.groups.edit.title')}
        </Button>
      }
    >
      <GroupForm form={form} />
    </Modal>
  )
}

export default observer(EditGroup)
